import {
  GoogleAddressSelectField,
  GridItem,
  TextInputField,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { Field, useField, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { CountrySelect } from "./CountrySelect";
import { ProvinceSelect } from "./ProvinceSelect";
import { filterSegment } from "./utils";
import constants from "../../../constants";
import { customFieldLabels } from "../../utils/address-utils";

type AddressInputProps = {
  nestedFieldName?: string;
  isWithoutUnit?: boolean;
};

export function AddressInput({
  nestedFieldName,
  isWithoutUnit,
}: AddressInputProps) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const getFieldName = (field: string) =>
    nestedFieldName ? `${nestedFieldName}.${field}` : field;

  const countryFieldName = getFieldName("country");
  const addressFieldName = getFieldName("address");
  const unitFieldName = getFieldName("unit");
  const cityFieldName = getFieldName("city");
  const countyFieldName = getFieldName("county");
  const provinceFieldName = getFieldName("province_state");
  const postalCodeFieldName = getFieldName("postal_code");

  const [countryField] = useField(countryFieldName);
  const labels = customFieldLabels[countryField.value] ?? customFieldLabels.CA;

  const handleAddressSelect = (
    address: google.maps.GeocoderAddressComponent[]
  ) => {
    const country = filterSegment(address, ["country"]);
    setFieldValue(countryFieldName, country?.short_name || "");

    const unit = filterSegment(address, ["subpremise", "premise"]);
    setFieldValue(unitFieldName, unit?.short_name || "");

    const cityTown = filterSegment(address, [
      "locality",
      "sublocality",
      "postal_town",
    ]);
    setFieldValue(cityFieldName, cityTown?.long_name ?? "");

    const stateProv = filterSegment(address, ["administrative_area_level_1"]);
    setFieldValue(provinceFieldName, stateProv?.short_name || "");

    const county = filterSegment(address, ["administrative_area_level_2"]);
    setFieldValue(countyFieldName, county?.short_name || "");

    const zipPostal = filterSegment(address, ["postal_code"]);
    setFieldValue(postalCodeFieldName, zipPostal?.long_name || "");
  };

  return (
    <>
      <GridItem>
        <CountrySelect
          testID={
            nestedFieldName
              ? `${slugify(nestedFieldName)}-country-select`
              : "address-history-country-select"
          }
          name={countryFieldName}
          label={t("Country")}
          onChange={() => setFieldValue(provinceFieldName, "")}
          isRequired
        />
      </GridItem>

      <GridItem>
        <Field
          isRequired
          name={addressFieldName}
          label={t("Address")}
          region={countryField.value}
          as={GoogleAddressSelectField}
          apiKey={constants?.VITE_GOOGLE_MAPS_API_KEY}
          onSelectionChange={handleAddressSelect}
          testID={
            nestedFieldName
              ? `${slugify(nestedFieldName)}-address-input`
              : "address-history-address-input"
          }
        />
      </GridItem>

      {!isWithoutUnit && (
        <GridItem>
          <Field
            name={unitFieldName}
            label={labels.unit}
            as={TextInputField}
            testID={
              nestedFieldName
                ? `${slugify(nestedFieldName)}-unit-input`
                : "address-history-unit-input"
            }
          />
        </GridItem>
      )}

      <GridItem>
        <Field
          testID={
            nestedFieldName
              ? `${slugify(nestedFieldName)}-city-input`
              : "address-history-city-input"
          }
          name={cityFieldName}
          label={labels.city}
          as={TextInputField}
          isRequired
        />
      </GridItem>

      {["US", "GB"].includes(countryField.value) && (
        <GridItem>
          <Field
            name={countyFieldName}
            testID={
              nestedFieldName
                ? `${slugify(nestedFieldName)}-county-input`
                : "address-history-county-input"
            }
            label={labels.county}
            as={TextInputField}
            isRequired
          />
        </GridItem>
      )}

      <GridItem span={8}>
        <ProvinceSelect
          name={provinceFieldName}
          testID={
            nestedFieldName
              ? `${slugify(nestedFieldName)}-province-select`
              : "address-history-province-select"
          }
          label={labels.province_state}
          country={countryField.value}
        />
      </GridItem>
      <GridItem span={4}>
        <Field
          name={postalCodeFieldName}
          label={labels.postal_code}
          as={TextInputField}
          isRequired
          testID={
            nestedFieldName
              ? `${slugify(nestedFieldName)}-postal-code-input`
              : "address-history-postal-code-input"
          }
        />
      </GridItem>
    </>
  );
}
